/**
 * The library, answered by the search index, once something is typed.
 *
 * The same view and filters as the database query (searchShapeFor), ordered
 * by relevance. What a role may find is in the signed key, not here: a
 * reader's key returns no owner whatever is asked of it
 * (functions/src/algolia/algoliaClient.ts, readerSearchShape).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchKey } from '../../search/useSearchKey';
import { runSearch } from '../../search/useSearch';
import { bestSnippet } from '../../search/highlight';
import type { SearchHit } from '../../search/types';
import { POLICY_STATUSES, type PolicyStatus } from '../../../types/policy';
import { ownersFor, type LibraryRow } from './libraryPage';
import type { SearchShape } from './libraryQuery';

/** A search hit as a row of the same table. Nothing of an owner is taken from the hit. */
export function rowFromHit(hit: SearchHit & { nextReviewAt?: number }): LibraryRow {
  const status = (POLICY_STATUSES as readonly string[]).includes(hit.status) ? (hit.status as PolicyStatus) : 'draft';
  const snippet = bestSnippet(hit);
  return {
    id: hit.policyId,
    policyNumber: hit.policyNumber ?? '',
    title: hit.title ?? '(untitled)',
    status,
    revisionStatus: null,
    departmentNames: hit.departmentName ? [hit.departmentName] : [],
    categoryName: hit.categoryName ?? null,
    documentTypeName: hit.documentTypeName ?? null,
    reviewDate: typeof hit.nextReviewAt === 'number' && hit.nextReviewAt > 0 ? new Date(hit.nextReviewAt).toISOString().slice(0, 10) : null,
    match: {
      title: hit._highlightResult?.title?.value ?? null,
      snippet: snippet.source === 'none' ? null : snippet.value,
      source: snippet.source,
    },
    owner: null,
  };
}

export interface LibrarySearchPage {
  rows: LibraryRow[];
  /** Every policy that matched. */
  total: number;
  /** How many of those can be paged to; past this the list says to narrow the search. */
  reachable: number;
  lastPage: number;
  tooMany: boolean;
}

export function useLibrarySearch(orgId: string | null, text: string, shape: SearchShape | null, page: number, withOwners: boolean) {
  const enabled = shape !== null && Boolean(orgId);
  const { data: key, isLoading: keyLoading, error: keyError } = useSearchKey(enabled);
  const trimmed = text.trim();
  const shapeKey = useMemo(() => JSON.stringify(shape), [shape]);

  const result = useQuery({
    queryKey: ['library-search', orgId, key?.indexName, trimmed, shapeKey, page, withOwners],
    enabled: enabled && Boolean(key),
    // The last page stays up while the next page of the SAME search loads, so
    // the table does not blink. Never across searches: the answer to the last
    // words typed, shown under the new ones, is a wrong answer for as long as it lasts.
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[3] === trimmed && previousQuery?.queryKey[4] === shapeKey ? previous : undefined),
    queryFn: async (): Promise<LibrarySearchPage> => {
      const found = await runSearch({
        key: key!, query: trimmed, facets: {}, hitsPerPage: shape!.hitsPerPage, page, enforceCeiling: true,
        extraFacetFilters: shape!.facetFilters, numericFilters: shape!.numericFilters,
      });
      const rows = found.hits.map(rowFromHit);
      // Ownership comes from the database, for people who are shown it; never from the index.
      if (withOwners && rows.length > 0) {
        const owners = await ownersFor(orgId!, rows.map((r) => r.id));
        for (const row of rows) row.owner = owners.get(row.id) ?? null;
      }
      return { rows, total: found.totalHits, reachable: found.reachableHits, lastPage: found.lastPage, tooMany: found.tooMany };
    },
  });

  // Named fields, not a spread of the query result: spreading reads every
  // property of it, including the promise the library exposes, which rejects
  // on a failed search with nobody waiting on it.
  return {
    data: result.data,
    isLoading: result.isLoading || keyLoading,
    isFetching: result.isFetching,
    error: result.error ?? keyError ?? null,
    refetch: result.refetch,
  };
}
